import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import {
  BUILDER_TYPES,
  BUILDER_CUSTOMER_STATUSES,
  BUILDER_ACCOUNT_STATUSES,
} from '../data/mockData';
import {
  Search,
  Phone,
  Mail,
  ArrowRight,
  Building2,
  Users,
  MessageSquare,
  Pencil,
  MapPin,
  CreditCard,
  BadgeCheck,
  Briefcase,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { cn } from '../lib/utils';

const BUILDER_STATUS_COLORS = {
  tendering:   'bg-blue-100 text-blue-700',
  quoted:      'bg-purple-100 text-purple-700',
  likely:      'bg-emerald-100 text-emerald-700',
  won:         'bg-green-100 text-green-700',
  lost:        'bg-red-100 text-red-700',
  'on-hold':   'bg-yellow-100 text-yellow-700',
};

const ACCOUNT_STATUS_COLORS = {
  good:     'bg-green-100 text-green-700',
  on_hold:  'bg-amber-100 text-amber-700',
  overdue:  'bg-red-100 text-red-700',
  inactive: 'bg-slate-100 text-slate-600',
};

function BuilderStatusBadge({ status }) {
  const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
  const color = BUILDER_STATUS_COLORS[status] || 'bg-gray-100 text-gray-700';
  return (
    <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded-full capitalize', color)}>
      {label}
    </span>
  );
}

function AccountStatusBadge({ status }) {
  if (!status) return null;
  const option = BUILDER_ACCOUNT_STATUSES.find(s => s.id === status);
  const color = ACCOUNT_STATUS_COLORS[status] || 'bg-slate-100 text-slate-600';
  return (
    <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded-full', color)}>
      {option?.name || status}
    </span>
  );
}

function getPrimaryContact(builder) {
  if (Array.isArray(builder.contacts) && builder.contacts.length > 0) return builder.contacts[0];
  if (builder.contact || builder.email || builder.phone) {
    return { name: builder.contact || '', email: builder.email || '', phone: builder.phone || '', role: '' };
  }
  return null;
}

const EMPTY_FORM = {
  name: '',
  address: '',
  builderType: '',
  customerStatus: '',
  accountStatus: '',
  creditLimit: '',
};

function EditBuilderDialog({ builder, opportunityId, onClose, onSave }) {
  const [form, setForm] = useState({
    name: builder.name || '',
    address: builder.address || '',
    builderType: builder.builderType || '',
    customerStatus: builder.customerStatus || '',
    accountStatus: builder.accountStatus || '',
    creditLimit: builder.creditLimit !== undefined && builder.creditLimit !== null && builder.creditLimit !== ''
      ? String(builder.creditLimit)
      : '',
  });

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name: form.name.trim() || builder.name,
      address: form.address.trim(),
      builderType: form.builderType,
      customerStatus: form.customerStatus,
      accountStatus: form.accountStatus,
      creditLimit: form.creditLimit !== '' ? parseFloat(form.creditLimit) || '' : '',
    });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Builder</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-1">
          {/* Builder name */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Builder Name</label>
            <Input
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="Builder name"
            />
          </div>

          {/* Address */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Address</label>
            <Input
              value={form.address}
              onChange={e => set('address', e.target.value)}
              placeholder="Street address, suburb, state, postcode"
            />
          </div>

          {/* Builder Type */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Builder Type</label>
            <Select value={form.builderType} onValueChange={v => set('builderType', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select builder type…" />
              </SelectTrigger>
              <SelectContent>
                {BUILDER_TYPES.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Customer Status */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Customer Status</label>
            <Select value={form.customerStatus} onValueChange={v => set('customerStatus', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Existing or new lead…" />
              </SelectTrigger>
              <SelectContent>
                {BUILDER_CUSTOMER_STATUSES.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Account Status */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Account Status</label>
            <Select value={form.accountStatus} onValueChange={v => set('accountStatus', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Account standing…" />
              </SelectTrigger>
              <SelectContent>
                {BUILDER_ACCOUNT_STATUSES.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Credit Limit */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Credit Limit ($)</label>
            <Input
              type="number"
              min="0"
              step="500"
              value={form.creditLimit}
              onChange={e => set('creditLimit', e.target.value)}
              placeholder="e.g. 50000"
            />
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">Save Changes</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function BuildersModule() {
  const { state, actions } = useApp();
  const { opportunities } = state;
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBuilderId, setSelectedBuilderId] = useState(null);
  const [editingBuilder, setEditingBuilder] = useState(null);

  // Derive a flat list of all builders enriched with parent context
  const allBuilders = useMemo(() =>
    opportunities.flatMap(opp =>
      (opp.builders || []).map(b => ({
        ...b,
        opportunityName: opp.name,
        opportunityId: opp.id,
        opportunityStatus: opp.status,
      }))
    ),
    [opportunities]
  );

  const normalizedQuery = searchQuery.toLowerCase();
  const filteredBuilders = useMemo(() =>
    allBuilders.filter(b =>
      b.name.toLowerCase().includes(normalizedQuery) ||
      b.opportunityName.toLowerCase().includes(normalizedQuery) ||
      (getPrimaryContact(b)?.name || '').toLowerCase().includes(normalizedQuery) ||
      (getPrimaryContact(b)?.email || '').toLowerCase().includes(normalizedQuery) ||
      (b.address || '').toLowerCase().includes(normalizedQuery) ||
      (b.builderType || '').toLowerCase().includes(normalizedQuery)
    ),
    [allBuilders, normalizedQuery]
  );

  const selectedBuilder = allBuilders.find(b => b.id === selectedBuilderId) || null;

  const handleViewInCRM = (builder) => {
    actions.setCurrentOpportunity(builder.opportunityId);
    actions.setCurrentBuilder(builder.id);
    actions.setActiveModule('crm');
  };

  const handleSaveBuilder = (updates) => {
    if (!editingBuilder) return;
    actions.updateBuilder(editingBuilder.opportunityId, editingBuilder.id, updates);
    setEditingBuilder(null);
  };

  return (
    <div className="h-full flex" data-testid="builders-module">

      {/* Left: Builder list */}
      <div className="w-72 border-r border-border flex flex-col bg-white shrink-0">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="font-semibold text-base mb-3">All Builders</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search builders…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 h-8 text-sm"
            />
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {filteredBuilders.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-sm">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                {allBuilders.length === 0 ? 'No builders yet' : 'No results'}
              </div>
            ) : (
              filteredBuilders.map(builder => {
                const primary = getPrimaryContact(builder);
                return (
                  <button
                    key={builder.id}
                    className={cn(
                      'w-full text-left px-3 py-2.5 rounded-md hover:bg-muted/50 transition-colors',
                      selectedBuilderId === builder.id && 'bg-accent'
                    )}
                    onClick={() => setSelectedBuilderId(builder.id)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate">{builder.name}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        {builder.accountStatus && <AccountStatusBadge status={builder.accountStatus} />}
                        <BuilderStatusBadge status={builder.status} />
                      </div>
                    </div>
                    {primary?.name && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{primary.name}</p>
                    )}
                    {builder.builderType && (
                      <p className="text-xs text-muted-foreground/60 truncate">{builder.builderType}</p>
                    )}
                    <p className="text-xs text-muted-foreground/70 truncate italic">{builder.opportunityName}</p>
                  </button>
                );
              })
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Right: Builder detail */}
      {selectedBuilder ? (
        <div className="flex-1 flex flex-col overflow-hidden bg-muted/20">

          {/* Header */}
          <div className="bg-white border-b border-border px-5 py-3 shrink-0 flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg font-semibold">{selectedBuilder.name}</h1>
                <BuilderStatusBadge status={selectedBuilder.status} />
                {selectedBuilder.accountStatus && (
                  <AccountStatusBadge status={selectedBuilder.accountStatus} />
                )}
                {selectedBuilder.customerStatus && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    {BUILDER_CUSTOMER_STATUSES.find(s => s.id === selectedBuilder.customerStatus)?.name || selectedBuilder.customerStatus}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                Opportunity: <span className="font-medium text-foreground">{selectedBuilder.opportunityName}</span>
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button size="sm" variant="outline" onClick={() => setEditingBuilder(selectedBuilder)}>
                <Pencil className="w-3.5 h-3.5 mr-1.5" />
                Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleViewInCRM(selectedBuilder)}>
                View in CRM
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex-1 overflow-hidden">
            <Tabs defaultValue="overview" className="h-full flex flex-col">
              <TabsList className="mx-4 mt-3 w-auto self-start">
                <TabsTrigger value="overview">
                  <Building2 className="w-4 h-4 mr-1.5" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="contacts">
                  <Users className="w-4 h-4 mr-1.5" />
                  Contacts ({(selectedBuilder.contacts || []).length})
                </TabsTrigger>
                <TabsTrigger value="activities">
                  <MessageSquare className="w-4 h-4 mr-1.5" />
                  Activities ({(selectedBuilder.activities || []).length})
                </TabsTrigger>
              </TabsList>

              {/* Overview tab */}
              <TabsContent value="overview" className="flex-1 overflow-auto px-4 py-3">
                <div className="max-w-lg space-y-4">

                  {/* Profile fields */}
                  <div className="bg-white border border-border rounded-lg divide-y divide-border">
                    {selectedBuilder.address ? (
                      <div className="flex items-start gap-3 p-3">
                        <MapPin className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                        <div>
                          <p className="text-xs text-muted-foreground">Address</p>
                          <p className="text-sm font-medium">{selectedBuilder.address}</p>
                        </div>
                      </div>
                    ) : null}

                    {selectedBuilder.builderType ? (
                      <div className="flex items-start gap-3 p-3">
                        <Briefcase className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                        <div>
                          <p className="text-xs text-muted-foreground">Builder Type</p>
                          <p className="text-sm font-medium">{selectedBuilder.builderType}</p>
                        </div>
                      </div>
                    ) : null}

                    {selectedBuilder.customerStatus ? (
                      <div className="flex items-start gap-3 p-3">
                        <BadgeCheck className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                        <div>
                          <p className="text-xs text-muted-foreground">Customer Status</p>
                          <p className="text-sm font-medium">
                            {BUILDER_CUSTOMER_STATUSES.find(s => s.id === selectedBuilder.customerStatus)?.name || selectedBuilder.customerStatus}
                          </p>
                        </div>
                      </div>
                    ) : null}

                    {selectedBuilder.accountStatus ? (
                      <div className="flex items-start gap-3 p-3">
                        <BadgeCheck className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                        <div>
                          <p className="text-xs text-muted-foreground">Account Status</p>
                          <p className="text-sm font-medium">
                            {BUILDER_ACCOUNT_STATUSES.find(s => s.id === selectedBuilder.accountStatus)?.name || selectedBuilder.accountStatus}
                          </p>
                        </div>
                      </div>
                    ) : null}

                    {selectedBuilder.creditLimit !== '' && selectedBuilder.creditLimit !== undefined && selectedBuilder.creditLimit !== null ? (
                      <div className="flex items-start gap-3 p-3">
                        <CreditCard className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                        <div>
                          <p className="text-xs text-muted-foreground">Credit Limit</p>
                          <p className="text-sm font-medium">
                            ${Number(selectedBuilder.creditLimit).toLocaleString('en-AU', { minimumFractionDigits: 0 })}
                          </p>
                        </div>
                      </div>
                    ) : null}

                    {/* Fallback if nothing is filled in */}
                    {!selectedBuilder.address && !selectedBuilder.builderType && !selectedBuilder.customerStatus && !selectedBuilder.accountStatus && (selectedBuilder.creditLimit === '' || selectedBuilder.creditLimit == null) && (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        <Building2 className="w-6 h-6 mx-auto mb-1 opacity-40" />
                        No profile details yet.{' '}
                        <button className="underline text-primary" onClick={() => setEditingBuilder(selectedBuilder)}>Edit builder</button>
                        {' '}to add them.
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Contacts tab */}
              <TabsContent value="contacts" className="flex-1 overflow-auto px-4 py-3">
                {(selectedBuilder.contacts || []).length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground text-sm">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    No contacts on this builder
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(selectedBuilder.contacts || []).map((contact, idx) => (
                      <div key={contact.id || idx} className="bg-white border border-border rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold shrink-0">
                            {(contact.name || '?')[0].toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{contact.name || 'Unnamed'}</p>
                            {contact.role && <p className="text-xs text-muted-foreground">{contact.role}</p>}
                          </div>
                        </div>
                        {(contact.email || contact.phone) && (
                          <div className="mt-2 flex flex-wrap gap-3">
                            {contact.email && (
                              <a href={`mailto:${contact.email}`} className="flex items-center gap-1 text-xs text-primary hover:underline">
                                <Mail className="w-3 h-3" />
                                {contact.email}
                              </a>
                            )}
                            {contact.phone && (
                              <a href={`tel:${contact.phone}`} className="flex items-center gap-1 text-xs text-primary hover:underline">
                                <Phone className="w-3 h-3" />
                                {contact.phone}
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Activities tab */}
              <TabsContent value="activities" className="flex-1 overflow-auto px-4 py-3">
                {(selectedBuilder.activities || []).length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground text-sm">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    No activities logged
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(selectedBuilder.activities || []).map((activity, idx) => (
                      <div key={activity.id || idx} className="bg-white border border-border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <Badge variant="outline" className="text-[10px] capitalize">{activity.type}</Badge>
                          <span className="text-xs text-muted-foreground">
                            {activity.createdAt ? new Date(activity.createdAt).toLocaleDateString('en-AU') : ''}
                          </span>
                        </div>
                        <p className="text-sm">{activity.content || activity.notes}</p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <div className="text-center">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Select a builder to view details</p>
          </div>
        </div>
      )}

      {/* Edit Builder Dialog */}
      {editingBuilder && (
        <EditBuilderDialog
          builder={editingBuilder}
          opportunityId={editingBuilder.opportunityId}
          onClose={() => setEditingBuilder(null)}
          onSave={handleSaveBuilder}
        />
      )}
    </div>
  );
}
